import { z } from "zod/v4";
import {
  CameraSubject,
  ClientApiFeaturePathParams,
  CollectionResponse,
  defineResource,
  defineEndpoint,
} from "@visioni/a3s-client-api";
import { isoDatetimeToDateString, isoDate, stringToInt, delimited } from "@visioni/a3s-codecs";
import { CLIP_SOURCES } from "../fleet-services/media-db/schema";
import { FEATURE_NAME } from "../constants";

export type ListClipsPathParams = ClientApiFeaturePathParams;

/**
 * Query parameters for list clips request.
 */
export const ListClipsQueryParams = z.object({
  startTime: isoDatetimeToDateString,
  endTime: isoDatetimeToDateString,
  source: delimited(",")
    .pipe(z.array(z.enum(CLIP_SOURCES)))
    .optional(),
  limit: stringToInt.pipe(z.int().positive().max(100)).optional(),
  cursor: z.string().optional(),
});

export type ListClipsQueryParams = z.output<typeof ListClipsQueryParams>;

export const ClipVideo = z.object({
  url: z.string(),
});

export type ClipVideo = z.output<typeof ClipVideo>;

export const ClipItem = z.object({
  subject: CameraSubject,
  startTime: z.string(),
  durationMs: z.number(),
  /** @deprecated Use video.url instead. Kept for backwards compatibility with deployed apps. */
  videoUrl: z.string().optional(),
  video: ClipVideo.optional(),
  thumbnailUrl: z.string().optional(),
});

export type ClipItem = z.output<typeof ClipItem>;

export const ListClipsResponse = CollectionResponse(ClipItem);

export type ListClipsResponse = z.output<typeof ListClipsResponse>;

// ==================== Resources & Endpoints ====================

export const clipsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "clips",
});

export const listClipsEndpoint = defineEndpoint(clipsResource, {
  responseSchema: ListClipsResponse,
  querySchema: ListClipsQueryParams,
});

// ==================== Hourly Counts ====================

export const CountClipsByHourQueryParams = z.object({
  date: isoDate,
  timezone: z.string(),
  source: delimited(",")
    .pipe(z.array(z.enum(CLIP_SOURCES)))
    .optional(),
});

export type CountClipsByHourQueryParams = z.output<typeof CountClipsByHourQueryParams>;

export const HourCount = z.object({
  hour: z.number(),
  count: z.number(),
  totalDurationMs: z.number(),
});

export type HourCount = z.output<typeof HourCount>;

export const CountClipsByHourResponse = z.object({
  data: z.array(HourCount),
});

export type CountClipsByHourResponse = z.output<typeof CountClipsByHourResponse>;

export const clipCountsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "clips/counts",
});

export const countClipsByHourEndpoint = defineEndpoint(clipCountsResource, {
  responseSchema: CountClipsByHourResponse,
  querySchema: CountClipsByHourQueryParams,
});

// ==================== Range Count ====================

/**
 * Bounds are absolute instants, not a date plus a timezone as
 * {@link CountClipsByHourQueryParams} takes. The caller asking this question
 * is counting the clips inside a window it has already resolved — a clip
 * compilation's requested range — and sends the same bounds it would send to
 * `POST /compilation`, so the count and the job cover exactly the same window.
 */
export const CountClipsInRangeQueryParams = z.object({
  startTime: isoDatetimeToDateString,
  endTime: isoDatetimeToDateString,
  source: delimited(",")
    .pipe(z.array(z.enum(CLIP_SOURCES)))
    .optional(),
});

export type CountClipsInRangeQueryParams = z.output<typeof CountClipsInRangeQueryParams>;

export const ClipRangeCount = z.object({
  count: z.number().int().nonnegative(),
  totalDurationMs: z.number().nonnegative(),
});

export type ClipRangeCount = z.output<typeof ClipRangeCount>;

export const CountClipsInRangeResponse = z.object({
  data: ClipRangeCount,
});

export type CountClipsInRangeResponse = z.output<typeof CountClipsInRangeResponse>;

/**
 * Separate from {@link clipCountsResource} (`clips/counts`, hour-of-day
 * buckets for one calendar day) because the two answer different questions and
 * neither's response shape can serve the other: hour-of-day buckets cannot
 * express a window spanning several days.
 */
export const clipRangeCountResource = defineResource({
  feature: FEATURE_NAME,
  resource: "clips/count",
});

export const countClipsInRangeEndpoint = defineEndpoint(clipRangeCountResource, {
  responseSchema: CountClipsInRangeResponse,
  querySchema: CountClipsInRangeQueryParams,
});
